// @ts-nocheck
import { useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { Tables } from "@/integrations/supabase/types";

type Notification = Tables<"user_notifications">;

// Generate a short notification beep using Web Audio API
function playNotificationSound() {
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, audioCtx.currentTime); // A5 note
    gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 1);

    oscillator.start(audioCtx.currentTime);
    oscillator.stop(audioCtx.currentTime + 1);
  } catch (e) {
    // Audio not supported or blocked by browser
    console.warn("Could not play notification sound:", e);
  }
}

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const prevUnreadRef = useRef<number | null>(null);

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("user_notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as Notification[];
    },
    enabled: !!user?.id,
  });

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // Play sound when unread count increases
  useEffect(() => {
    if (prevUnreadRef.current !== null && unreadCount > prevUnreadRef.current) {
      playNotificationSound();
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  // Realtime subscription for new notifications
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "user_notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("user_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("id", notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) return;
      
      const { error } = await supabase
        .from("user_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    },
  });

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
  };
}

export function getNotificationIcon(type: Notification["notification_type"]) {
  switch (type) {
    case "REPORT_COMPLETED":
      return "FileText";
    case "RISK_RATING_CHANGED":
    case "COMPANY_RATING_CHANGED":
    case "urba_monitoring_change":
      return "AlertTriangle";
    case "SECTOR_RISK_CHANGED":
      return "Activity";
    case "WELCOME_NEW_USER":
      return "UserPlus";
    case "INVOICE_GENERATED":
      return "Receipt";
    case "PAYMENT_SUCCESS":
      return "CheckCircle";
    case "PAYMENT_FAILED":
      return "XCircle";
    case "IMPORT_COFACE_COMPLETED":
      return "Upload";
    case "SUPPORT_MESSAGE":
      return "MessageSquare";
    default:
      return "Bell";
  }
}

export function getNotificationColor(type: Notification["notification_type"]) {
  switch (type) {
    case "REPORT_COMPLETED":
      return "text-blue-500";
    case "RISK_RATING_CHANGED":
    case "COMPANY_RATING_CHANGED":
    case "urba_monitoring_change":
      return "text-orange-500";
    case "SECTOR_RISK_CHANGED":
      return "text-purple-500";
    case "WELCOME_NEW_USER":
      return "text-green-500";
    case "INVOICE_GENERATED":
      return "text-purple-500";
    case "PAYMENT_SUCCESS":
      return "text-green-500";
    case "PAYMENT_FAILED":
      return "text-red-500";
    case "IMPORT_COFACE_COMPLETED":
      return "text-mint";
    case "SUPPORT_MESSAGE":
      return "text-blue-600";
    default:
      return "text-muted-foreground";
  }
}